<template>
  <div class="date-request-container">
    <Alert
      v-if="
        !flow.isPrivateEventRequest.value &&
        dateRequestSidebarRequestPreferredLocation === 'user_location'
      "
      class="my-8 kft-alert--warning"
      type="warning"
      :content="t('dateRequest.eventSuggestions.cantBeHeldAtCustomLocation')"
    />
    <!-- Form Content - shown when not in available dates layer -->
    <form
      v-if="!showAvailableDatesLayer"
      ref="contactFormRef"
      class="w-100 pb-4"
      data-test-id="contact-form"
      @submit.prevent.stop="submit()"
    >
      <DateRequestNumberOfTickets
        v-if="!flow.isInstantDateRequestSimplifiedUi.value"
        :number-of-tickets="form.numberOfTickets"
        :private-event-min-tickets="privateEventMinTickets"
        :min-tickets="minTickets"
        :is-instant-only-event="
          !flow.isDateRequestEnabledForTheEvent.value &&
          eventGetters.isInstantBookingActive(eventData)
        "
        :is-from-event-page="flow.isFromEventPage.value"
        :instant-booking-min-tickets="
          eventGetters.getMinInstantBookingTickets(eventData || {})
        "
        :event-data="eventData"
        :invalid-keys="invalidKeys"
        :errors="errors"
        :auto-focus="!flow.isInstantDateRequestSimplifiedUi.value"
        :show-ticket-validation="showTicketValidation"
        :show-out-of-scope-error="
          showTicketValidation &&
          !flow.isDateRequestApplicable.value &&
          !flow.isPrivateEventRequest.value &&
          !flow.isFromEventPage.value
        "
        :show-event-inactive-helper="
          showTicketValidation &&
          flow.isFromEventPage.value &&
          !flow.isDateRequestEnabledForTheEvent.value &&
          !flow.isPrivateEventRequest.value &&
          !eventGetters.isInstantBookingActive(eventData)
        "
        :purchase-max-tickets="flow.dateRequestPurchaseMaxTickets.value"
        :show-party-size-purchase-private-gap="
          showTicketValidation && flow.isPartySizeInPurchasePrivateGap.value
        "
        :show-party-size-above-date-request-max="
          showTicketValidation && flow.isPartySizeAboveDateRequestMax.value
        "
        @update:number-of-tickets="(v) => (form.numberOfTickets = v)"
        @clear-errors="(field) => clearErrors(field)"
        @number-of-tickets-user-change="handleTicketCountCommitted"
      />

      <!-- Date inputs, contact info, adaptive fields and consent sit directly
      under tickets in the same order for both private and date-request. -->
      <DateRequestDateSlots
        v-if="showUnifiedRequestFields"
        v-model:dates="form.dates"
        :min-date-formatted="dateSlots.minDateFormatted.value"
        :start-time="dateSlots.datePickerStartTime.value"
        :start-date="dateSlots.minDatePickerStart.value"
        :disabled-week-days="dateSlotsDisabledWeekDays"
        :is-date-disabled="dateSlots.isStandardCalendarDateDisabled"
        :is-slot-available="
          (userDate) =>
            !flow.isPrivateEventRequest.value &&
            dateSlots.isStandardSlotDateAvailable(userDate, availableDates)
        "
        :errors="errors"
        :invalid-keys="invalidKeys"
        @clear-errors="(field) => clearErrors(field)"
      />
      <DateRequestInstantBookingForm
        v-else-if="flow.isInstantDateRequestSimplifiedUi.value"
        v-model="form"
        :event-data="eventData || {}"
        :available-dates="availableDates"
        :errors="errors"
        :invalid-keys="invalidKeys"
        :private-event-min-tickets="privateEventMinTickets"
        @clear-errors="(field) => clearErrors(field)"
        @number-of-tickets-user-change="handleTicketCountCommitted"
        @instant-booking-added="onInstantBookingAddedToCart"
      />

      <template v-if="showUnifiedRequestFields">
        <DateRequestUserInfo
          v-model="form"
          :is-private-event-request="flow.isPrivateEventRequest.value"
          :errors="errors"
          :invalid-keys="invalidKeys"
          @clear-errors="(field) => clearErrors(field)"
        />
        <DateRequestTypeSelect
          v-if="!flow.isPrivateEventRequest.value"
          v-model="form.dateType"
          :tooltip-enabled="eventGetters.getIsTeamEvent(eventData)"
          :errors="errors"
          :invalid-keys="invalidKeys"
          @clear-errors="(field) => clearErrors(field)"
        />
        <div v-if="flow.isPrivateEventRequest.value" class="mt-4">
          <PrivateEventDate
            v-model="form"
            :errors="errors"
            :invalid-keys="invalidKeys"
            @clear-errors="(field) => clearErrors(field)"
          />
        </div>
        <DateRequestContactConfirmation
          v-model="canUserBeContactedChecked"
          :is-confirmed="isContactConfirmationChecked"
        />
      </template>

      <p
        v-if="submitError"
        class="mt-4 text-red text-sm"
        data-test-id="date-request-submit-error"
      >
        {{ submitError }}
      </p>

      <DateRequestActionButtons
        :button-text="buttonText"
        :disable-actions="
          loading ||
          calendarLoading ||
          flow.isPartySizeInPurchasePrivateGap.value ||
          flow.isPartySizeAboveDateRequestMax.value
        "
        :show-continue-button="
          showAvailableDates &&
          !showAvailableDatesLayer &&
          !flow.isPrivateEventRequest.value &&
          flow.canPurchaseTicketsViaDateRequest.value
        "
        :show-add-to-cart-button="
          showAvailableDatesLayer &&
          (selectedAvailableDates?.length ?? 0) > 0 &&
          flow.canPurchaseTicketsViaDateRequest.value
        "
        :show-submit-button="
          !flow.isInstantDateRequestSimplifiedUi.value &&
          (!showAvailableDates ||
            flow.isPrivateEventRequest.value ||
            !flow.canPurchaseTicketsViaDateRequest.value)
        "
        @continue="showAvailableDatesLayer = true"
        @add-to-cart="availableDatesSidebarRef?.addSelectedDatesToCart()"
        @submit="submit()"
      />
    </form>

    <DateRequestMatchingDatesTab
      ref="availableDatesSidebarRef"
      :event-data="eventData"
      :form="form"
      :show-matching-dates-layer="showAvailableDatesLayer"
      :enable-matching-dates-purchase="
        flow.canPurchaseTicketsViaDateRequest.value
      "
      @add-to-cart="handleAddToCart"
      @close="handleRequestOtherDates"
      @availability-changed="
        (hasAvailableDates: boolean) => (showAvailableDates = hasAvailableDates)
      "
    />
  </div>
</template>

<script lang="ts" setup>
import 'vue3-toastify/dist/index.css'
import DateRequestNumberOfTickets from '~/components/date-request/DateRequestNumberOfTickets.vue'
import DateRequestDateSlots from '~/components/date-request/DateRequestDateSlots.vue'
import DateRequestTypeSelect from '~/components/date-request/DateRequestTypeSelect.vue'
import DateRequestContactConfirmation from '~/components/date-request/DateRequestContactConfirmation.vue'
import DateRequestActionButtons from '~/components/date-request/DateRequestActionButtons.vue'
import { useDateRequestFlow } from '~/composables/useDateRequestFlow'
import { useDateRequestFormState } from '~/composables/useDateRequestFormState'
import { useDateRequestDateSlots } from '~/composables/useDateRequestDateSlots'
import { useDateRequestInstantSync } from '~/composables/useDateRequestInstantSync'
import { useDateRequestSubmission } from '~/composables/useDateRequestSubmission'
import {
  focusFirstDateRequestDateField,
  hasLostDateRequestFormFocus,
  shouldRestoreDateRequestFocusAfterTicketCommit,
} from '~/utils/dateRequestFocus'

const { t } = useI18n()
const eventData = useEventData()
const {
  dateRequestSidebarRequestPreferredLocation,
  dateRequestSuggestedTicketCount,
} = useUiState()

const emit = defineEmits<{
  'on-complete-request': [dateRequestId?: string]
  onCloseDateRequest: []
  'on-complete-private-request': []
}>()

const { form } = useDateRequestFormState()
const flow = useDateRequestFlow(form)
const dateSlots = useDateRequestDateSlots()

const {
  errors,
  invalidKeys,
  clearErrors,
  submit,
  loading,
  buttonText,
  canUserBeContactedChecked,
  isContactConfirmationChecked,
  submitError,
} = useDateRequestSubmission(form, flow, { emit })

useDateRequestInstantSync(form, flow, { clearErrors })

const availableDatesSidebarRef = ref<{
  selectedAvailableDates: unknown[]
  calendarLoading: boolean
  availableDates: unknown[]
  addSelectedDatesToCart: () => void
} | null>(null)

const showAvailableDates = ref(false)
const showAvailableDatesLayer = ref(false)
const contactFormRef = ref<HTMLElement | null>(null)

/** Private and date-request share one layout; instant-booking keeps its own. */
const showUnifiedRequestFields = computed(
  () =>
    flow.isPrivateEventRequest.value ||
    (flow.isDateRequestApplicable.value &&
      !flow.isInstantDateRequestSimplifiedUi.value)
)

/** Private events aren't limited to the event's public availability days. */
const dateSlotsDisabledWeekDays = computed(() =>
  flow.isPrivateEventRequest.value
    ? []
    : dateSlots.getStandardDisabledWeekDays()
)

const handleTicketCountCommitted = (event?: FocusEvent) => {
  flow.userHasCommittedTicketCount.value = true

  nextTick(() => {
    const shouldRestore =
      shouldRestoreDateRequestFocusAfterTicketCommit(event) ||
      hasLostDateRequestFormFocus(contactFormRef.value)

    if (!shouldRestore) {
      return
    }

    focusFirstDateRequestDateField({
      isPrivateEventRequest: flow.isPrivateEventRequest.value,
      isInstantDateRequestSimplifiedUi:
        flow.isInstantDateRequestSimplifiedUi.value,
      formRoot: contactFormRef.value,
    })
  })
}

const selectedAvailableDates = computed(
  () => availableDatesSidebarRef.value?.selectedAvailableDates
)
const calendarLoading = computed(
  () => availableDatesSidebarRef.value?.calendarLoading ?? false
)
const availableDates = computed(
  () =>
    (availableDatesSidebarRef.value?.availableDates ??
      []) as import('~/composables/types/event').EventDate[]
)

const privateEventMinTickets = computed(() =>
  eventGetters.getPrivateEventMinTicketsThreshold(eventData.value)
)

const minTickets = computed(() => {
  if (eventGetters.isDateRequestActive(eventData.value)) {
    return eventGetters.getMinDateRequestTickets(eventData.value)
  } else if (eventGetters.isInstantBookingActive(eventData.value)) {
    return eventGetters.getMinInstantBookingTickets(eventData.value)
  }
  return privateEventMinTickets.value
})

const showTicketValidation = computed(() => {
  if (form.value.numberOfTickets == null) {
    return false
  }

  const buyBoxCountSynced =
    dateRequestSuggestedTicketCount.value != null &&
    dateRequestSuggestedTicketCount.value >= 1

  return flow.userHasCommittedTicketCount.value || buyBoxCountSynced
})

watch(flow.canPurchaseTicketsViaDateRequest, (canPurchase) => {
  if (!canPurchase && showAvailableDatesLayer.value) {
    showAvailableDatesLayer.value = false
  }
})

const handleAddToCart = () => {
  emit('onCloseDateRequest')
}

const onInstantBookingAddedToCart = () => {
  emit('onCloseDateRequest')
}

const handleRequestOtherDates = () => {
  showAvailableDatesLayer.value = false
}

defineExpose({
  form,
  showMatchingDatesLayer: showAvailableDatesLayer,
  closeMatchingDatesLayer: () => {
    showAvailableDatesLayer.value = false
  },
  openMatchingDatesLayer: () => {
    showAvailableDatesLayer.value = true
  },
})
</script>

<style lang="postcss" scoped>
.date-request-container :deep(input:not([type='checkbox']):not([type='radio'])),
.date-request-container :deep(select),
.date-request-container :deep(textarea),
.date-request-container :deep(.dp__input) {
  @apply font-lexend;
}

.date-request-container
  :deep(
    input:not([type='checkbox']):not([type='radio']):focus:not(.border-red)
  ),
.date-request-container :deep(select:focus:not(.border-red)),
.date-request-container :deep(textarea:focus:not(.border-red)),
.date-request-container :deep(.dp__input:focus:not(.border-red)) {
  @apply rounded-md border-[1.5px] border-teal-300 bg-white outline-none;
  box-shadow: 0 0 0 3px rgb(214 240 242 / 16%);
}
</style>
